import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { process } from '@progress/kendo-data-query';
import { Search, LayoutGrid, List, ChevronDown, ChevronUp, User, Calendar, Clock, X } from 'lucide-react';
import { authorizations } from '../../data/mockData';
import { authorizationApi } from '../../api/authorizationApi';
import { isApiConfigured } from '../../api/axiosClient';
import { LoadingSpinner, ErrorCard } from '../../components/LoadingSpinner';
import { ColumnMenu } from '../../components/ColumnMenu';
import { CardsPagination } from '../../components/CardsPagination';
import { useAuth } from '../../auth/AuthContext';
import { userApi } from '../../api/userApi';



const AuthorizationInquiryPage = () => {
    const { user } = useAuth();
    const [billingProviderId, setBillingProviderId] = useState("");
    const [memberId, setMemberId] = useState('');
    const [authNumber, setAuthNumber] = useState('');
    const [groupId, setGroupId] = useState('');
    const [fromDate, setFromDate] = useState('2010-01-01');
    const today = new Date().toISOString().split('T')[0];
    const [toDate, setToDate] = useState(today);
    const [results, setResults] = useState([]);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
    const [error, setError] = useState(null);
    const [userTaxIds, setUserTaxIds] = useState([]);
    const [groups, setGroups] = useState([]);
    // Track which row ids are expanded — mirrors the ClaimStatusPage pattern.
    const [expandedItems, setExpandedItems] = useState([]);
    // Paging / sorting state passed into the Grid
    const [dataState, setDataState] = useState({ skip: 0, take: 10, sort: [], filter: null });
    // 'cards' | 'table' — matches the User Management view toggle
    const [viewMode, setViewMode] = useState('cards');
    // Free-text search applied across all visible columns. Hoisted above the
    // view-mode conditional so the same input filters both Cards and Table.
    const [searchText, setSearchText] = useState('');
    const excelExportRef = useRef(null);

    const formatDate = (value) => {
        if (!value) return '';

        const [mm, dd, yyyy] = value.split('-');
        return `${mm}/${dd}/${yyyy}`;
    };

    const parseDate = (str) => {
        if (!str) return null;

        const [month, day, year] = str.split(/[-/]/);;
        return new Date(year, month - 1, day);
    };

    const handleSearch = async () => {
        setLoading(true);
        setError(null);
        setExpandedItems([]);
        setDataState({ skip: 0, take: 10, sort: [], filter: null });

        if (isApiConfigured()) {
            try {
                const params = {};
                //if (memberId)? params.memberId :'';
                params.memberId = memberId ? memberId : '';
                params.authNumber = authNumber ? authNumber : '';
                params.group = groupId ? groupId : '';
                params.from = fromDate ? fromDate : '';
                params.to = toDate ? toDate : '';
                //params.taxId = user?.taxId ? user?.taxId : billingProviderId;
                params.taxId = billingProviderId;

                if (!params.taxId?.trim()) {
                    setError('Billing Provider Id is required');
                    setLoading(false);
                    return;
                }
                

              //  alert("JSONPARMAS:" + JSON.stringify(params));
                const res = await authorizationApi.search(params);

                if (res.success)
                {                   

                    const formattedGridData = (res?.data?.Referrals || []).map((item, index) => ({
                        id: index + 1, // required for dataItemKey
                        authNumber: item?.Auth_Number ?? '',
                        memberName: item?.Member_Name ?? '',
                        //memberId: item?.Subscriber_ID ?? '',
                        memberId: Number(item?.Subscriber_ID?.replace("-", "")) ?? '',
                        service: item?.Auth_Type ?? '', 
                        priority: item?.Urgency_Type ?? '', 
                        status: item?.Auth_Status ?? '',
                        procedureCode: item?.Procedure_Code ?? '',
                        diagnosisCode: item?.Diagnostic_Code ?? '',
                        requestDate: parseDate(item?.Request_From),
                        decisionDate: parseDate(item?.Approved_From), 
                        expirationDate: parseDate(item?.Approved_Thru)

                    }));

                   

                    setResults(formattedGridData);                    
                }
                else {
                    setError('Data not found');
                }

                setSearched(true);
                setLoading(false);
            } catch (err) {
                setError(err.message || 'Failed to search authorizations');
            }
        } else {
            // Mock delay so the "Searching…" button state is perceptible.
            await new Promise((r) => setTimeout(r, 350));
            let filtered = [...authorizations];

            if (memberId) {
                filtered = filtered.filter((a) =>
                    a.memberId.toLowerCase().includes(memberId.toLowerCase())
                );
            }
            if (authNumber) {
                filtered = filtered.filter((a) =>
                    a.authNumber.toLowerCase().includes(authNumber.toLowerCase())
                );
            }
            if (groupId) {
                filtered = filtered.filter((a) => !a.groupNumber || a.groupNumber === groupId);
            }
            if (fromDate) {
                filtered = filtered.filter((a) => a.requestDate >= fromDate);
            }
            if (toDate) {
                filtered = filtered.filter((a) => a.requestDate <= toDate);
            }

            setResults(filtered);
        }

        setSearched(true);
        setLoading(false);
    };


    useEffect(() => {
        if (user?.userName) {

            getUserTaxIds(user?.userName);
        }
    }, [user?.userName]);

    const getUserTaxIds = async (userName) => {

        if (isApiConfigured()) {
            setPageLoading(true);

            try {
                var res = await userApi.getUsersTaxIds({
                    userName: userName
                });


                if (res.success) {
                    setUserTaxIds(res?.data?.TaxIds);
                    console.log("UserTaxIds get successfully");
                }
                else {
                    setUserTaxIds([]);
                    console.log(res.message);
                }

                setPageLoading(false);

            } catch (err) {
                console.log("Error at getUserTaxIds : " + err);
                setPageLoading(false);

            } finally {

            }
        }
    };

    const handleClear = () => {
        setMemberId('');
        setAuthNumber('');
        setGroupId('');
        setFromDate('');
        setToDate('');
        setResults([]);
        setSearched(false);
        setError(null);
        setExpandedItems([]);
        setDataState({ skip: 0, take: 10, sort: [], filter: null });
        setSearchText('');
        setBillingProviderId("");
    };

    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(255,255,255,0.7)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }
    };

    const StatusCell = (props) => {
        const value = props.dataItem.status;
        const badgeClass = value.toLowerCase();
        return (
            <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
                <span className={`status-badge ${badgeClass}`}>{value}</span>
            </td>
        );
    };

    // Detail panel shown when a row is expanded. Visual style mirrors the
    // Claim Status page: soft teal background tint + 3px teal left border +
    // a mini table format so it reads as a structured sub-record.
    const DetailComponent = (props) => {
        const item = props.dataItem;
        return (
            <div style={{
                padding: '16px 24px',
                background: 'rgba(13,148,136,0.03)',
                borderLeft: '3px solid #0D9488',
            }}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: 12,
                }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#1A1D21' }}>
                        Authorization Details
                    </div>
                    <span style={{ fontSize: 12, color: '#6B7280' }}>
                        {item.authNumber}
                    </span>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                        <tr style={{
                            background: '#F1F5F9',
                            textTransform: 'uppercase',
                            fontSize: 11, fontWeight: 600, color: '#6B7280',
                        }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Member ID</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Procedure Code</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Diagnosis Code</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Expiration</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr style={{ borderBottom: '1px solid #E8ECF0' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600 }}>{item.memberId || '—'}</td>
                            <td style={{ padding: '8px 12px' }}>{item.procedureCode || '—'}</td>
                            <td style={{ padding: '8px 12px' }}>{item.diagnosisCode || '—'}</td>
                            <td style={{ padding: '8px 12px' }}>{item.expirationDate?.toLocaleDateString('en-US', {
                                month: '2-digit',
                                day: '2-digit',
                                year: 'numeric'
                            }) || '-'}</td>
                        </tr>
                    </tbody>
                </table>
                {item.notes && (
                    <div style={{ marginTop: 12, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                        <strong style={{ color: '#6B7280', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Notes
                        </strong>
                        <div style={{ marginTop: 4 }}>{item.notes}</div>
                    </div>
                )}
            </div>
        );
    };

    // Toggle one row's expanded state by id. Matches the ClaimStatusPage
    // pattern — expanded state lives in its own array (not inside results),
    // and the Grid gets `dataItemKey="id"` so Kendo tracks rows correctly
    // across re-renders.
    const onExpandChange = (e) => {
        const id = e.dataItem.id;
        setExpandedItems((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    // Apply the free-text search before passing to process(). Matches against
    // every visible column so users get fast narrowing without thinking about
    // which field to type into.
    const filteredResults = useMemo(() => {
        if (!searchText.trim()) return results;
        const q = searchText.trim().toLowerCase();
        return results.filter((a) => {
            return [
                a.authNumber, a.memberName, a.memberId, a.service, a.priority,
                a.status, a.requestDate?.toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric'
                }), a.decisionDate?.toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric'
                }), a.expirationDate,
                a.procedureCode, a.diagnosisCode, a.notes,
            ]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q));
        });
    }, [results, searchText]);

    // Reset the page to 0 whenever the search text changes so the user
    // doesn't end up looking at an empty page that no longer exists.
    useEffect(() => {
        setDataState((s) => ({ ...s, skip: 0 }));
    }, [searchText]);

    useEffect(() => {
        fetchGroups();
    },[]);

    const fetchGroups = async () => {
        const res = await userApi.groups();

        if (res.success) {
            setGroups(res.data.Groups);
        }
    }

    // Pre-process: spread each result with its current `expanded` flag, then
    // run through Kendo's `process()` so sort / filter / paging all apply.
    const gridData = process(
        filteredResults.map((item) => ({
            ...item,
            expanded: expandedItems.includes(item.id),
        })),
        dataState
    );


    
    //console.log("-----------------------------------------------------");
    //console.log("take:", dataState.take);
    //console.log("skip:", dataState.skip);
    //console.log("filteredResults:", filteredResults.length);
    //console.log("gridData.total:", gridData.total);
    //console.log("gridData.data.length:", gridData.data.length);
    //console.log("-----------------------------------------------------");


    const RequestDateCell = (props) => {
        
        const value = props?.dataItem?.requestDate?.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
        return (
            <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
                {value || <span style={{ color: '#9CA3AF' }}>--</span>}
            </td>
        );
    };

    const DecisionDateCell = (props) => {

        const value = props?.dataItem?.decisionDate?.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
        return (
            <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
                {value || <span style={{ color: '#9CA3AF' }}>Pending</span>}
            </td>
        );
    };

    return (
        <div className="main-content">
            {pageLoading && (
                <div style={styles.overlay}>
                    <LoadingSpinner data-cy="page-loader-profile" message="Please wait..." fullPage="true" />
                </div>
            )}
            <div className="page-header">
                <div>
                    <h1>Authorization Inquiry</h1>
                    <p>
                        The status of the Prior Authorization is based on end of business
                        yesterday
                    </p>
                </div>
            </div>

            <div className="card">
                <h3>Search Authorizations</h3>
                <div className="form-grid">
                    {/*<div className="form-group">*/}
                    {/*    <label>Group ID</label>*/}
                    {/*    <select value={groupId} onChange={e => setGroupId(e.target.value)}>*/}
                            
                    {/*        <option value="">All Groups</option>*/}
                    {/*        {groups.map(group => (*/}
                    {/*            <option key={group.Group_Id} value={group.Group_Id}>{group.Group_Id}</option>*/}
                    {/*        ))}*/}
                    {/*    </select>*/}
                    {/*</div>*/}
                    {
                        user?.userRole?.trim().toLowerCase() ==='super admin' && (
                            <div className="form-group">
                                <label className="required">Billing Provider ID</label>
                                <input
                                    type="text"
                                    value={billingProviderId}
                                    onChange={(e) => setBillingProviderId(e.target.value)}
                                    placeholder="Enter Billing Provider ID"
                                />
                            </div>
                        )
                    }

                    {
                        user?.userRole?.trim().toLowerCase() !== 'super admin' && (
                            <div className="form-group">
                                <label className="required">Billing Provider ID</label>
                                <select
                                    value={billingProviderId}
                                    onChange={(e) => setBillingProviderId(e.target.value)}
                                >
                                    <option value="">Select Billing Provider ID</option>
                                    {userTaxIds?.map((id, index) => (
                                        <option key={id} value={id}>
                                            {id}
                                        </option>
                                    ))}
                                </select>
                                  
                            </div>
                        )
                    }
                    

                    <div className="form-group">
                        <label>Member ID</label>
                        <input
                            type="text"
                            value={memberId}
                            onChange={(e) => setMemberId(e.target.value)}
                            placeholder="e.g. SND-100234"
                        />
                    </div>

                    <div className="form-group">
                        <label>Authorization Number</label>
                        <input
                            type="text"
                            value={authNumber}
                            onChange={(e) => setAuthNumber(e.target.value)}
                            placeholder="e.g. AUTH-2026-00312"
                        />
                    </div>

                    <div className="form-group">
                        <label>From Date</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>To Date</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                        />
                    </div>
                </div>

                <div className="form-actions">
                    <button
                        name="btnSearchAuth"
                        className="btn-primary"
                        onClick={handleSearch}
                        disabled={loading}
                        aria-busy={loading}
                    >
                        {loading ? 'Searching…' : 'Search'}
                    </button>
                    <button className="btn-secondary" onClick={handleClear} disabled={loading}>
                        Clear
                    </button>
                </div>
            </div>

            {loading && <LoadingSpinner message="Searching authorizations..." />}
            {error && <ErrorCard message={error} onRetry={handleSearch} />}

            {!loading && !error && searched && results.length === 0 && (
                <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
                    <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }}><Search size={36} color="#9CA3AF" strokeWidth={1.5} /></div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                        No Authorizations Found
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                        Try adjusting your search criteria or date range
                    </div>
                </div>
            )}

            {!loading && !error && searched && results.length > 0 && (
                <>
                    {/* Result counter + Search + View toggle + Excel export */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 12,
                            padding: '0 2px',
                            gap: 12,
                            flexWrap: 'wrap',
                        }}
                    >
                        <span style={{ fontSize: 14, color: '#6B7280', fontWeight: 500 }}>
                            Showing {filteredResults.length} of {results.length} authorization{results.length !== 1 ? 's' : ''}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            {/* Free-text search — works in both Cards and Table views */}
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '6px 10px', background: '#fff',
                                border: '1px solid #E8ECF0', borderRadius: 10,
                                minWidth: 220,
                            }}>
                                <Search size={14} color="#94A3B8" />
                                <input
                                    name="txtSearch"
                                    type="text"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    placeholder="Search results…"
                                    style={{
                                        flex: 1, border: 'none', outline: 'none',
                                        fontSize: 13, color: '#1A1D21',
                                        background: 'transparent', fontFamily: 'inherit',
                                        minWidth: 0,
                                    }}
                                />
                                {searchText && (
                                    <button
                                        name="btnClearSearch"
                                        onClick={() => setSearchText('')}
                                        style={{
                                            border: 'none', background: 'transparent',
                                            cursor: 'pointer', padding: 0,
                                            color: '#94A3B8', display: 'inline-flex',
                                        }}
                                        title="Clear search"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                            <div style={{
                                display: 'flex', background: '#F1F5F9', borderRadius: 10,
                                padding: 3, gap: 2,
                            }}>
                                <button
                                    onClick={() => setViewMode('cards')}
                                    style={toggleBtnStyle(viewMode === 'cards')}
                                    title="Card view"
                                    aria-pressed={viewMode === 'cards'}
                                >
                                    <LayoutGrid size={14} strokeWidth={2.5} />
                                    Cards
                                </button>
                                <button
                                    onClick={() => setViewMode('table')}
                                    style={toggleBtnStyle(viewMode === 'table')}
                                    title="Table view"
                                    aria-pressed={viewMode === 'table'}
                                >
                                    <List size={14} strokeWidth={2.5} />
                                    Table
                                </button>
                            </div>
                            <Button
                                themeColor="primary"
                                fillMode="outline"
                                onClick={() => {
                                    if (excelExportRef.current) {
                                        // Export the filtered (post-search) result set so what
                                        // the user sees is exactly what gets exported.
                                        excelExportRef.current.save(filteredResults);
                                    }
                                }}
                                style={{ fontSize: 13 }}
                            >
                                Export to Excel
                            </Button>
                        </div>
                    </div>

                    {/* ExcelExport lives outside the view-conditional so the button
              works in both Cards and Table views. Data is passed to save()
              so the Grid doesn't need to be a child. */}
                    <ExcelExport
                        ref={excelExportRef}
                        fileName="AuthorizationInquiry_Export"
                    >
                        <ExcelExportColumn field="authNumber" title="Auth #" width={160} />
                        <ExcelExportColumn field="memberName" title="Member Name" width={170} />
                        <ExcelExportColumn field="memberId" title="Member ID" width={140} />
                        <ExcelExportColumn field="service" title="Service" width={220} />
                        <ExcelExportColumn field="priority" title="Priority" width={110} />
                        <ExcelExportColumn field="status" title="Status" width={120} />
                        <ExcelExportColumn field="requestDate" title="Request Date" width={130} />
                        <ExcelExportColumn field="decisionDate" title="Decision Date" width={130} />
                        <ExcelExportColumn field="expirationDate" title="Expiration Date" width={130} />
                        <ExcelExportColumn field="procedureCode" title="Procedure Code" width={130} />
                        <ExcelExportColumn field="diagnosisCode" title="Diagnosis Code" width={130} />
                        <ExcelExportColumn field="notes" title="Notes" width={300} />
                    </ExcelExport>

                    {viewMode === 'cards' ? (
                        filteredResults.length === 0 ? (
                            <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
                                <div style={{ fontSize: 14, color: '#6B7280' }}>
                                    No authorizations match your search.
                                </div>
                            </div>
                        ) : (
                            <>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                    gap: 16,
                                }}>
                                    {/* gridData.data is the post-search, post-sort, post-page
                      slice — Cards view uses the same slice so pagination,
                      sorting, and search behave identically across views. */}
                                    {gridData.data.map((a) => (
                                        <AuthCard
                                            key={a.id}
                                            auth={a}
                                            expanded={expandedItems.includes(a.id)}
                                            onToggle={() =>
                                                setExpandedItems((prev) =>
                                                    prev.includes(a.id)
                                                        ? prev.filter((x) => x !== a.id)
                                                        : [...prev, a.id]
                                                )
                                            }
                                        />
                                    ))}
                                </div>
                                <CardsPagination
                                    total={gridData.total}
                                    skip={dataState.skip}
                                    take={dataState.take}
                                    onPageChange={({ skip, take }) => setDataState((s) => ({ ...s, skip, take }))}
                                    pageSizes={[5, 10, 20]}
                                />
                            </>
                        )
                    ) : (
                        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                            <div className="grid-scroll-wrapper">
                                <Grid
                                        data={gridData}                                      
                                        scrollable="scrollable"
                                    sortable={true}
                                    resizable={true}
                                    reorderable={true}
                                    pageable={{ pageSizes: [5, 10, 20] }}
                                    filterable={false}
                                    dataItemKey="id"
                                    expandField="expanded"
                                    onExpandChange={onExpandChange}
                                    detail={DetailComponent}
                                    onDataStateChange={(e) => setDataState(e.dataState)}
                                    {...dataState}
                                    style={{ border: 'none' }}
                                    >
                                    
                                    <GridColumn field="authNumber" title="Auth #" width="180px" columnMenu={ColumnMenu} />
                                    <GridColumn field="memberName" title="Member Name" width="170px" columnMenu={ColumnMenu} />
                                             <GridColumn field="memberId" title="Member Id" width="170px" columnMenu={ColumnMenu} />
                                    <GridColumn field="service" title="Service" width="220px" columnMenu={ColumnMenu} />
                                    <GridColumn field="priority" title="Priority" width="110px" columnMenu={ColumnMenu} />
                                        <GridColumn field="procedureCode" title="Procedure Code" width="110px" columnMenu={ColumnMenu} />
                                        <GridColumn field="diagnosisCode" title="Diagnosis Code" width="110px" columnMenu={ColumnMenu} />
                                    <GridColumn
                                        field="status"
                                        title="Status"
                                        width="150px"
                                        columnMenu={ColumnMenu}
                                        cells={{ data: StatusCell }}
                                    />
                                        <GridColumn field="requestDate" title="Request Date" width="140px" columnMenu={ColumnMenu} format="{0:MM/dd/yyyy}" cells={{ data: RequestDateCell }} filter="date" />
                                        
                                    <GridColumn
                                        field="decisionDate"
                                        title="Decision Date"
                                        width="140px"
                                        columnMenu={ColumnMenu}
                                            format="{0:MM/dd/yyyy}" cells={{ data: DecisionDateCell }}
                                            filter="date"

                                            
                                    />
                                </Grid>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

/* ── Card view for authorizations ──────────────────────────── */
const AuthCard = ({ auth, expanded, onToggle }) => {
    const status = (auth.status || '').toLowerCase();
    const priorityColor = auth.priority === 'Urgent' ? '#DC2626' : '#2563EB';

    return (
        <div style={{
            background: '#fff',
            border: '1px solid #E8ECF0',
            borderRadius: 16,
            padding: 18,
            transition: 'all 0.2s',
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
        }}
            onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#CBD5E1';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)';
                e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#E8ECF0';
                e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.03)';
                e.currentTarget.style.transform = 'translateY(0)';
            }}
        >
            {/* Header: auth # + status */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0D9488', fontFamily: 'monospace' }}>
                    {auth.authNumber}
                </div>
                <span className={`status-badge ${status}`}>{auth.status}</span>
            </div>

            {/* Member + service */}
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 15, fontWeight: 700, color: '#1A1D21', marginBottom: 4 }}>
                    <User size={14} color="#6B7280" strokeWidth={2} />
                    {auth.memberName}
                </div>
                <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.4 }}>
                    {auth.service}
                </div>
            </div>

            {/* Priority pill */}
            <div>
                <span style={{
                    display: 'inline-block',
                    padding: '3px 10px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    background: `${priorityColor}14`,
                    color: priorityColor,
                    letterSpacing: 0.3,
                }}>
                    {auth.priority}
                </span>
            </div>

            {/* Date rows */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: '#6B7280' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Calendar size={12} strokeWidth={2} />
                    <span style={{ fontWeight: 600 }}>Request:</span>
                    <span style={{ color: '#1A1D21' }}>{auth.requestDate?.toLocaleDateString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric'
                    })}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Clock size={12} strokeWidth={2} />
                    <span style={{ fontWeight: 600 }}>Decision:</span>
                    <span style={{
                        color: auth.decisionDate?.toLocaleDateString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric'
                        }) ? '#1A1D21' : '#9CA3AF' }}>
                        {auth.decisionDate?.toLocaleDateString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric'
                        }) || 'Pending'}
                    </span>
                </div>
            </div>

            {/* Expand toggle */}
            <button
                name="btnShowHideDetailsCard"
                onClick={onToggle}
                style={{
                    marginTop: 'auto',
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #E8ECF0',
                    background: expanded ? 'rgba(13,148,136,0.05)' : '#fff',
                    color: '#0D9488',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 5,
                    fontFamily: 'inherit',
                }}
            >
                {expanded ? <ChevronUp size={13} strokeWidth={2.5} /> : <ChevronDown size={13} strokeWidth={2.5} />}
                {expanded ? 'Hide details' : 'Show details'}
            </button>

            {/* Expanded details */}
            {expanded && (
                <div style={{
                    marginTop: 4,
                    padding: '12px 14px',
                    borderRadius: 10,
                    background: 'rgba(13,148,136,0.03)',
                    borderLeft: '3px solid #0D9488',
                    fontSize: 12,
                    color: '#374151',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                }}>
                    <div>
                        <strong style={{ color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Member ID</strong>
                        <div>{auth.memberId || '—'}</div>
                    </div>
                    <div>
                        <strong style={{ color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Procedure</strong>
                        <div>{auth.procedureCode || '—'}</div>
                    </div>
                    <div>
                        <strong style={{ color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Diagnosis</strong>
                        <div>{auth.diagnosisCode || '—'}</div>
                    </div>
                    <div>
                        <strong style={{ color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Expiration</strong>
                        <div>{auth.expirationDate?.toLocaleDateString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric'
                        }) || '-'}</div>
                    </div>
                    {auth.notes && (
                        <div>
                            <strong style={{ color: '#6B7280', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Notes</strong>
                            <div style={{ lineHeight: 1.5 }}>{auth.notes}</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const toggleBtnStyle = (active) => ({
    padding: '7px 12px', borderRadius: 8, border: 'none',
    background: active ? '#fff' : 'transparent',
    color: active ? '#0D9488' : '#6B7280',
    fontSize: 12, fontWeight: 700,
    cursor: 'pointer', display: 'inline-flex',
    alignItems: 'center', gap: 5,
    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
});

export default AuthorizationInquiryPage;
